import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { ListToolbar } from '../../Common/ListToolbar';
import StatusBadge from '../../Common/StatusBadge';
import '../Personell/employee-form.css';
import { Plus } from 'lucide-react';

const SelectEmployeeModal = ({ show, handleClose, handleSelect, employees, existingPayslips = [] }) => {
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Reset selections when modal opens/closes
    useEffect(() => {
        if (!show) {
            setSelectedEmployees([]);
            setSelectAll(false);
            setSearchTerm('');
        }
    }, [show]);

    // Create a Set of employee IDs that already have payslips
    const employeesWithPayslips = useMemo(() => {
        return new Set(existingPayslips.map(payslip => payslip.employeeId || payslip.employee_id));
    }, [existingPayslips]);

    // Filter employees based on search term
    const filteredEmployees = employees.filter(employee =>
        employee.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Separate employees into eligible and ineligible categories
    const { eligibleEmployees, ineligibleEmployees } = useMemo(() => {
        return filteredEmployees.reduce((acc, employee) => {
            const employeeId = employee.employee_id || employee.id;
            const hasContract = employee.has_active_contract;
            const alreadyHasPayslip = employeesWithPayslips.has(employeeId);

            if (hasContract && !alreadyHasPayslip) {
                acc.eligibleEmployees.push(employee);
            } else {
                acc.ineligibleEmployees.push({
                    ...employee,
                    ineligibleReason: alreadyHasPayslip ? 'already_added' : 'no_contract'
                });
            }
            return acc;
        }, { eligibleEmployees: [], ineligibleEmployees: [] });
    }, [filteredEmployees, employeesWithPayslips]);

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setSelectAll(isChecked);
        // Only select employees with active contracts
        setSelectedEmployees(isChecked ? eligibleEmployees.map(emp => emp.employee_id) : []);
    };

    const handleSelectChange = (employee, event) => {
        const employeeId = employee.employee_id || employee.id;
        const alreadyHasPayslip = employeesWithPayslips.has(employeeId);

        // Prevent selection if no active contract or already has payslip
        if (!employee.has_active_contract || alreadyHasPayslip) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        const value = employeeId;
        setSelectedEmployees(prevState => {
            const newState = prevState.includes(value)
                ? prevState.filter(id => id !== value)
                : [...prevState, value];

            // Update selectAll state based on all eligible employees being selected
            setSelectAll(newState.length === eligibleEmployees.length && eligibleEmployees.length > 0);
            return newState;
        });
    };

    return (
        <>
            <style>{`
                .sel-emp__intro {
                    margin: 0 0 1rem;
                    font-size: 0.875rem;
                    color: #6b7280;
                }

                .sel-emp__all {
                    display: flex;
                    align-items: center;
                    gap: 0.6rem;
                    margin: 0.75rem 0 0.5rem;
                    font-size: 0.8125rem;
                    font-weight: 600;
                    color: #4b5563;
                }

                .sel-emp__all .form-check-input { margin: 0; cursor: pointer; }
                .sel-emp__all label { margin: 0; cursor: pointer; }
                .sel-emp__all small { font-weight: 400; color: #6b7280; }

                .sel-emp__list {
                    max-height: 50vh;
                    overflow-y: auto;
                    border: 1px solid #e5e7eb;
                    border-radius: 0.625rem;
                }

                .sel-emp__row {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.625rem 0.875rem;
                    border-bottom: 1px solid #f1f3f7;
                    cursor: pointer;
                    transition: background-color 0.12s ease;
                }

                .sel-emp__row:last-child { border-bottom: 0; }

                /* Same tint as the selected sidebar item and the list row hover. */
                .sel-emp__row:hover,
                .sel-emp__row.is-selected { background: var(--rgba-primary-1); }

                .sel-emp__row.is-disabled {
                    cursor: not-allowed;
                    color: #9ca3af;
                }

                .sel-emp__row.is-disabled:hover { background: transparent; }
                .sel-emp__row.is-disabled .emp-avatar { opacity: 0.5; }

                .sel-emp__row .form-check-input { margin: 0; flex-shrink: 0; cursor: inherit; }

                .sel-emp__name {
                    flex: 1;
                    min-width: 0;
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: #111827;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .sel-emp__row.is-disabled .sel-emp__name { color: #9ca3af; }

                .sel-emp__empty {
                    padding: 2rem 1rem;
                    text-align: center;
                    font-size: 0.875rem;
                    color: #6b7280;
                }

                .sel-emp__count {
                    font-size: 0.8125rem;
                    color: #6b7280;
                }
            `}</style>

            <Modal show={show} onHide={handleClose} size="lg" className="emp-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Välj anställda till lönekörningen</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p className="sel-emp__intro">
                        Varje vald anställd får en lönespecifikation i den här perioden. Anställda utan
                        aktivt kontrakt, eller som redan har en lönespecifikation, kan inte väljas.
                    </p>

                    <ListToolbar
                        search={{
                            value: searchTerm,
                            onChange: setSearchTerm,
                            placeholder: 'Sök efter anställd…',
                            ariaLabel: 'Sök bland anställda',
                        }}
                    />

                    <div className="sel-emp__all">
                        <input
                            type="checkbox"
                            id="selectAll"
                            className="form-check-input"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            disabled={eligibleEmployees.length === 0}
                        />
                        <label htmlFor="selectAll">
                            Välj alla
                            {' '}
                            <small>
                                ({eligibleEmployees.length} tillgänglig{eligibleEmployees.length !== 1 ? 'a' : ''}
                                {ineligibleEmployees.length > 0 && `, ${ineligibleEmployees.length} kan inte väljas`})
                            </small>
                        </label>
                    </div>

                    <div className="sel-emp__list" role="list">
                        {filteredEmployees.length === 0 ? (
                            <div className="sel-emp__empty">
                                {searchTerm ? `Inget matchar "${searchTerm}".` : 'Inga anställda tillgängliga.'}
                            </div>
                        ) : (
                            filteredEmployees.map((employee) => {
                                const employeeId = employee.employee_id || employee.id;
                                const isSelected = selectedEmployees.includes(employeeId);
                                const hasContract = employee.has_active_contract;
                                const alreadyHasPayslip = employeesWithPayslips.has(employeeId);
                                const isDisabled = !hasContract || alreadyHasPayslip;
                                const initials = (employee.name || '')
                                    .split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase();

                                let reason = null;
                                if (alreadyHasPayslip) {
                                    reason = { tone: 'muted', label: 'Redan tillagd', title: 'Lönespecifikation finns redan för denna anställd i denna period' };
                                } else if (!hasContract) {
                                    reason = { tone: 'warning', label: 'Inget kontrakt', title: 'Anställd saknar aktivt kontrakt' };
                                }

                                return (
                                    <div
                                        key={employeeId}
                                        role="listitem"
                                        className={`sel-emp__row${isSelected ? ' is-selected' : ''}${isDisabled ? ' is-disabled' : ''}`}
                                        onClick={(e) => handleSelectChange(employee, e)}
                                        title={reason?.title}
                                    >
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            value={employeeId}
                                            checked={isSelected}
                                            disabled={isDisabled}
                                            onChange={(e) => handleSelectChange(employee, e)}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label={employee.name}
                                        />
                                        <span className="emp-avatar" aria-hidden="true">{initials || '—'}</span>
                                        <span className="sel-emp__name">{employee.name}</span>
                                        {reason && <StatusBadge tone={reason.tone}>{reason.label}</StatusBadge>}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer className="justify-content-between">
                    <span className="sel-emp__count">
                        {selectedEmployees.length > 0 ? `${selectedEmployees.length} valda` : 'Ingen vald'}
                    </span>
                    <div className="d-flex gap-2">
                        <button type="button" className="ll-btn ll-btn--secondary" onClick={handleClose}>
                            Avbryt
                        </button>
                        <button
                            type="button"
                            className="ll-btn ll-btn--primary"
                            onClick={() => { handleSelect(selectedEmployees); }}
                            disabled={selectedEmployees.length === 0}
                        >
                            <Plus />
                            Lägg till{selectedEmployees.length > 0 ? ` (${selectedEmployees.length})` : ''}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default SelectEmployeeModal;
